import { Reveal } from './Reveal';
import { LANDING_ANCHORS, LANDING_CONTACT, LANDING_FORM } from '../../domain/landingContent';
import { useLandingLeadForm } from '../hooks/useLandingLeadForm';

const INPUT =
  'min-h-11 w-full min-w-0 rounded-2xl border border-white/[0.08] bg-white/[0.04] px-4 py-3 text-white placeholder:text-slate-600 focus:border-orange-500/40 focus:outline-none focus:ring-2 focus:ring-orange-500/20';

/** Formulário de captura de leads com fallback WhatsApp. */
export function LandingLeadForm() {
  const { dados, atualizar, enviar, loading, sucesso, erro, whatsappFallbackUrl } =
    useLandingLeadForm();

  if (sucesso) {
    return (
      <section
        id={LANDING_ANCHORS.contato}
        className="mx-auto max-w-xl scroll-mt-24 px-4 py-24 sm:px-6 sm:py-32"
      >
        <Reveal>
          <div
            className="rounded-[2rem] border border-emerald-500/25 bg-emerald-500/10 px-6 py-12 text-center"
            role="status"
            aria-live="polite"
          >
            <h2 className="text-2xl font-bold text-white">{LANDING_FORM.successTitle}</h2>
            <p className="mt-3 text-sm text-emerald-200/80">{LANDING_FORM.successBody}</p>
          </div>
        </Reveal>
      </section>
    );
  }

  return (
    <section
      id={LANDING_ANCHORS.contato}
      className="mx-auto max-w-xl scroll-mt-24 px-4 py-24 sm:px-6 sm:py-32"
    >
      <Reveal className="text-center">
        <h2 className="text-3xl font-bold tracking-tight text-white sm:text-4xl">
          {LANDING_FORM.title}
        </h2>
        <p className="mt-3 text-sm text-slate-400">{LANDING_FORM.subcopy}</p>
      </Reveal>

      <Reveal delay={0.08} className="mt-10">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            void enviar();
          }}
          className="space-y-4 rounded-[2rem] border border-white/[0.06] bg-white/[0.03] p-6 sm:p-8"
          noValidate
        >
          <div>
            <label htmlFor="lead-nome" className="mb-1.5 block text-xs font-medium text-slate-400">
              Nome *
            </label>
            <input
              id="lead-nome"
              type="text"
              required
              autoComplete="name"
              value={dados.nome}
              onChange={(e) => atualizar('nome', e.target.value)}
              className={INPUT}
              placeholder="Seu nome"
            />
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label
                htmlFor="lead-restaurante"
                className="mb-1.5 block text-xs font-medium text-slate-400"
              >
                Restaurante / Bar
              </label>
              <input
                id="lead-restaurante"
                type="text"
                value={dados.restaurante}
                onChange={(e) => atualizar('restaurante', e.target.value)}
                className={INPUT}
                placeholder="Nome do estabelecimento"
              />
            </div>
            <div>
              <label htmlFor="lead-cidade" className="mb-1.5 block text-xs font-medium text-slate-400">
                Cidade
              </label>
              <input
                id="lead-cidade"
                type="text"
                value={dados.cidade}
                onChange={(e) => atualizar('cidade', e.target.value)}
                className={INPUT}
                placeholder="Sua cidade"
              />
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label
                htmlFor="lead-whatsapp"
                className="mb-1.5 block text-xs font-medium text-slate-400"
              >
                WhatsApp *
              </label>
              <input
                id="lead-whatsapp"
                type="tel"
                required
                autoComplete="tel"
                inputMode="tel"
                value={dados.whatsapp}
                onChange={(e) => atualizar('whatsapp', e.target.value)}
                className={INPUT}
                placeholder="(11) 99999-9999"
              />
            </div>
            <div>
              <label
                htmlFor="lead-telas"
                className="mb-1.5 block text-xs font-medium text-slate-400"
              >
                Nº de telas
              </label>
              <input
                id="lead-telas"
                type="number"
                min={1}
                max={99}
                value={dados.numTelas}
                onChange={(e) => atualizar('numTelas', e.target.value)}
                className={INPUT}
                placeholder="Ex: 2"
              />
            </div>
          </div>

          <div>
            <label htmlFor="lead-msg" className="mb-1.5 block text-xs font-medium text-slate-400">
              Mensagem
            </label>
            <textarea
              id="lead-msg"
              rows={3}
              value={dados.mensagem}
              onChange={(e) => atualizar('mensagem', e.target.value)}
              className={`${INPUT} resize-none`}
              placeholder="Conte um pouco sobre sua operação (opcional)"
            />
          </div>

          <label className="flex min-h-11 cursor-pointer items-start gap-3 text-xs text-slate-400">
            <input
              type="checkbox"
              checked={dados.lgpd}
              onChange={(e) => atualizar('lgpd', e.target.checked)}
              className="mt-0.5 h-5 w-5 shrink-0 rounded border-white/20 bg-white/5 text-orange-500 focus:ring-orange-500/30"
            />
            <span>
              Autorizo o uso dos meus dados para contato comercial, conforme a LGPD. Seus dados não
              serão compartilhados com terceiros.
            </span>
          </label>

          <p className="text-center text-xs text-slate-500">
            <a
              href={LANDING_CONTACT.whatsappUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-orange-400 hover:text-orange-300"
            >
              {LANDING_FORM.whatsappAlt}
            </a>
          </p>

          {erro && (
            <div className="space-y-2" role="alert" aria-live="assertive">
              <p className="text-sm text-red-400">{erro}</p>
              {whatsappFallbackUrl && (
                <a
                  href={whatsappFallbackUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-orange-400 hover:text-orange-300"
                >
                  Falar no WhatsApp
                </a>
              )}
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="inline-flex min-h-11 w-full items-center justify-center rounded-xl bg-gradient-to-r from-orange-500 to-rose-500 py-3.5 text-sm font-semibold text-white shadow-lg shadow-orange-600/25 transition-all hover:shadow-orange-500/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-400/50 disabled:opacity-60 active:scale-[0.98] motion-reduce:transition-none"
          >
            {loading ? 'Enviando…' : LANDING_FORM.submit}
          </button>
        </form>
      </Reveal>
    </section>
  );
}
